import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { TranslationContext } from "@is/constants";
import { useCollection, useConfiguration, useOnScreen } from "@is/hooks";
import { CollectionStyle } from "@is/types";

const useManageCollectionEditView = (
  collectionId: string,
  onRemove: () => void
) => {
  const { t } = useTranslation(TranslationContext.Settings);

  const {
    collection,
    isPending,
    updateCollection,
    updateCollectionDemo,
    updateCollectionCheckoutSuggestions,
    deleteStyleFromCollection,
    getStyles,
    addStyleToCollection,
  } = useCollection(collectionId);

  const { backgroundMedia } = useConfiguration({
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
  });

  const searchAreaRef = useRef<HTMLDivElement>(null);
  const searchFieldRef = useRef<HTMLInputElement>(null);
  const searchContentRef = useRef<HTMLDivElement>(null);

  const isSearchVisible = useOnScreen(searchContentRef);

  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
  const [collectionName, setCollectionName] = useState("");

  const [showSearchButton, setShowSearchButton] = useState(false);

  const isDiscoverYourOutfitsEnabled = useMemo(
    () => backgroundMedia?.discoverYourOutfits ?? false,
    [backgroundMedia]
  );

  const includedStyleVariants = useMemo(
    () => collection?.items.map((style) => style.variantId) ?? [],
    [collection]
  );

  const hasNameDifference = useMemo(
    () => collectionName !== collection?.name,
    [collection, collectionName]
  );

  const isDemoCollection = useMemo(
    () => collection?.isDemoCollection ?? false,
    [collection]
  );

  const isCheckoutSuggestionsCollection = useMemo(
    () => collection?.isCheckoutSuggestionsCollection ?? false,
    [collection]
  );

  // Brand-wide collections are managed from the admin dashboard. Store staff can open and
  // browse them, but every edit affordance is withheld — and the backend refuses the mutation
  // anyway, so offering a control here would only produce a failed request.
  const isBrandWide = useMemo(
    () => collection?.isBrandWide ?? false,
    [collection]
  );

  const handleCollectionModalOpen = useCallback(() => {
    setIsCollectionModalOpen(true);
    setCollectionName(collection?.name ?? "");
  }, [collection]);

  const handleCollectionModalClose = useCallback(() => {
    setIsCollectionModalOpen(false);
  }, []);

  const handleNameChange = useCallback(
    (value: string) => setCollectionName(value),
    []
  );

  const handleCollectionSave = useCallback(async () => {
    if (!collectionName) {
      return;
    }

    const updatedCollection = await updateCollection({ name: collectionName });
    if (updatedCollection) {
      setIsCollectionModalOpen(false);
    }
  }, [collectionName, updateCollection]);

  const handleCollectionReorder = useCallback(
    async (items: CollectionStyle[]) => {
      await updateCollection({
        items: items,
      });
    },
    [updateCollection]
  );

  const handleCollectionRemove = useCallback(() => {
    setIsCollectionModalOpen(false);
    onRemove();
  }, [onRemove]);

  const handleCollectionRemoveStyle = useCallback(
    async (index: number) => {
      await deleteStyleFromCollection(index);
    },
    [deleteStyleFromCollection]
  );

  const handleDemoToggle = useCallback(async () => {
    try {
      await updateCollectionDemo(!isDemoCollection);
    } catch (error) {
      console.error("Failed to update demo status:", error);
    }
  }, [isDemoCollection, updateCollectionDemo]);

  const handleCheckoutSuggestionsToggle = useCallback(async () => {
    try {
      await updateCollectionCheckoutSuggestions(
        !isCheckoutSuggestionsCollection
      );
    } catch (error) {
      console.error("Failed to update checkout suggestions status:", error);
    }
  }, [isCheckoutSuggestionsCollection, updateCollectionCheckoutSuggestions]);

  const scrollToItem = useCallback(() => {
    searchAreaRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    setTimeout(() => searchFieldRef.current?.focus(), 600);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowSearchButton(true);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  return {
    t,
    isPending,
    collection,
    collectionName,
    storedCollectionName: collection?.name,
    isCollectionModalOpen,
    includedStyleVariants,
    searchAreaRef,
    searchFieldRef,
    searchContentRef,
    isSearchVisible,
    showSearchButton,
    isDemoCollection,
    isCheckoutSuggestionsCollection,
    handleCollectionModalOpen,
    handleCollectionModalClose,
    handleNameChange,
    handleCollectionSave,
    handleCollectionReorder,
    handleCollectionRemove,
    handleCollectionRemoveStyle,
    handleDemoToggle,
    handleCheckoutSuggestionsToggle,
    getStyles,
    addStyleToCollection,
    scrollToItem,
    hasNameDifference,
    isDiscoverYourOutfitsEnabled,
    isBrandWide,
  };
};

export default useManageCollectionEditView;
